import { useMemo, useState } from "react";
import LedgerNumber from "@/components/LedgerNumber";
import { formatINR } from "@/lib/brand";
import YearBreakdown from "./YearBreakdown";
import CalculatorActions from "./CalculatorActions";

const EDU_PRESETS = [
  { label: "Engineering Degree (4 yrs total)", cost: 1600000, inflation: 10 },
  { label: "Medical Degree (MBBS, total)", cost: 5000000, inflation: 10 },
  { label: "MBA (2 yrs, total)", cost: 2500000, inflation: 10 },
  { label: "Custom", cost: "", inflation: 10 },
];

const HEALTH_PRESETS = [
  { label: "Angiography", cost: 40000, inflation: 12 },
  { label: "Angioplasty", cost: 350000, inflation: 12 },
  { label: "Bypass Surgery", cost: 450000, inflation: 12 },
  { label: "Major Surgery (general)", cost: 600000, inflation: 12 },
  { label: "Custom", cost: "", inflation: 12 },
];

export default function FutureCostCalculator() {
  const [category, setCategory] = useState("education");
  const presets = category === "education" ? EDU_PRESETS : HEALTH_PRESETS;
  const [presetIdx, setPresetIdx] = useState(0);
  const [cost, setCost] = useState(presets[0].cost || 0);
  const [inflation, setInflation] = useState(presets[0].inflation);
  const [years, setYears] = useState(10);

  const applyPreset = (idx, list = presets) => {
    setPresetIdx(idx);
    const p = list[idx];
    setCost(p.cost === "" ? 0 : p.cost);
    setInflation(p.inflation);
  };

  const switchCategory = (c) => {
    setCategory(c);
    const list = c === "education" ? EDU_PRESETS : HEALTH_PRESETS;
    applyPreset(0, list);
  };

  const future = useMemo(() => {
    const c = Number(cost) || 0;
    const r = (Number(inflation) || 0) / 100;
    const t = Number(years) || 0;
    return c * Math.pow(1 + r, t);
  }, [cost, inflation, years]);

  const milestones = useMemo(() => {
    const c = Number(cost) || 0;
    const r = (Number(inflation) || 0) / 100;
    const yrs = Number(years) || 0;
    const rows = [];
    for (let k = 1; k <= yrs; k++) {
      const v = c * Math.pow(1 + r, k);
      rows.push({
        year: k,
        primary: v,
        columns: [
          { value: formatINR(v) },
          { value: formatINR(Math.max(0, v - c)), accent: true },
        ],
      });
    }
    return rows;
  }, [cost, inflation, years]);

  const increase = future - Number(cost || 0);

  return (
    <div className="space-y-8">
      {/* Category toggle */}
      <div className="flex gap-2 border-b hairline pb-1" role="tablist" data-testid="future-category">
        {["education", "healthcare"].map((c) => (
          <button
            key={c}
            onClick={() => switchCategory(c)}
            className={`px-4 py-2 text-[14px] tracking-wide border-b-2 -mb-[1px] transition-colors ${
              category === c ? "border-brass text-brass-deep" : "border-transparent text-ink-soft hover:text-ink"
            }`}
            data-testid={`future-cat-${c}`}
          >
            {c === "education" ? "Education" : "Healthcare"}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-6">
          <label className="block">
            <span className="eyebrow block mb-2">Preset</span>
            <select
              value={presetIdx}
              onChange={(e) => applyPreset(Number(e.target.value))}
              className="fld" data-testid="future-preset"
            >
              {presets.map((p, i) => <option key={p.label} value={i}>{p.label}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="eyebrow block mb-2">Cost Today (₹)</span>
            <input
              type="number" value={cost} min={0}
              onChange={(e) => setCost(e.target.value === "" ? "" : Number(e.target.value))}
              className="fld num" data-testid="future-cost"
            />
          </label>

          <label className="block">
            <span className="eyebrow block mb-2">Assumed Annual Inflation (%)</span>
            <input
              type="number" value={inflation} step={0.5} min={0} max={30}
              onChange={(e) => setInflation(e.target.value === "" ? "" : Number(e.target.value))}
              className="fld num" data-testid="future-inflation"
            />
            <p className="text-[11px] text-ink-soft/85 leading-relaxed mt-2">
              Official CPI: education ~3–4%, health ~1.5–3.5%. Real-world: education 10–12%,
              healthcare 12–14%.
            </p>
          </label>

          <label className="block">
            <div className="flex items-center justify-between mb-2">
              <span className="eyebrow">Years From Now</span>
              <span className="num text-ink text-[14px]">{years} yrs</span>
            </div>
            <input
              type="range" min={1} max={30} value={years}
              onChange={(e) => setYears(Number(e.target.value))}
              className="w-full accent-[#14A5A5]" data-testid="future-years"
            />
            <div className="flex justify-between text-[11px] text-ink-soft/80 mt-1 num"><span>1</span><span>30</span></div>
          </label>
        </div>

        <div className="lg:col-span-7 card-paper p-6 md:p-8 ledger-rule" data-testid="future-result">
          <div className="eyebrow mb-2">Future Cost in {years} years</div>
          <div className="text-[38px] md:text-[48px] leading-none text-ink">
            <LedgerNumber value={future} testId="future-value" />
          </div>

          <div className="mt-8 divide-y hairline border-t hairline border-b">
            <div className="flex items-center justify-between py-3">
              <span className="eyebrow">Cost Today</span>
              <span className="num text-[16px] text-ink">{formatINR(cost)}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="eyebrow">Total Increase</span>
              <span className="num text-[16px] text-brass-deep">{formatINR(increase)}</span>
            </div>
          </div>
        </div>
      </div>

      <YearBreakdown
        rows={milestones}
        primaryLabel="Projected cost at end of each year"
        tableHeaders={["Year", "Projected Cost", "Increase over Today"]}
        testIdPrefix="future"
      />

      <CalculatorActions
        testIdPrefix="future"
        buildPayload={() => ({
          type: "future",
          title: "Future Cost of Education & Healthcare",
          one_liner: `a future-cost projection — ${category === "education" ? "an education" : "a healthcare"} cost of ${formatINR(cost)} today at ${inflation}% inflation, ${years} years from now`,
          inputs: [
            { label: "Category", value: category === "education" ? "Education" : "Healthcare" },
            { label: "Cost Today", value: formatINR(cost) },
            { label: "Assumed Annual Inflation", value: `${inflation}%` },
            { label: "Years From Now", value: `${years} years` },
          ],
          hero: { label: `Future Cost in ${years} years`, value: formatINR(future) },
          summary: [
            { label: "Cost Today", value: formatINR(cost) },
            { label: "Total Increase", value: formatINR(increase), accent: true },
          ],
          row_headers: ["Year", "Projected Cost", "Increase over Today"],
          rows: milestones.map((r) => [
            String(r.year), formatINR(r.primary), formatINR(r.primary - Number(cost || 0)),
          ]),
        })}
      />
    </div>
  );
}
